import { useCallback, useEffect } from 'react';

import {
    keepPreviousData,
    useInfiniteQuery,
    useQueryClient,
    type InfiniteData,
    type UseInfiniteQueryResult,
} from '@tanstack/react-query';

import { categories, type Category } from 'constants/categories';
import { CSRF_HEADER_NAME, CSRF_LOCAL_STORAGE_KEY } from 'constants/session';
import { createApiClient } from 'lib/api/createApiClient';
import { fetchWithAwsWaf } from 'lib/security/awsWaf';
import { useAuthStore } from 'lib/stores/authStore';
import type { EventPositionVo } from 'models';
import type { EventVo } from 'models/event';
import type { PortfolioEvent } from 'models/portfolioEvent';
import type {
    GetEventPositionsParams,
    GetEventPositionsResponse,
    GetEventsResponse,
    PositionStatus,
    SettlementStatus,
} from 'models/reqres';
import { usePortfolioEventsStore } from 'stores/zustand/portfolioEventsStore';
import { useSubAccountStore } from 'stores/zustand/subAccountStore';
import config from 'utils/config';
import { getLocalStorageWithExpiration } from 'utils/storage';

import { type SortPortfolioEventsOptions } from './portfolioEventsSort';
import { enrichCloseDates, toPortfolioEventsFromVos } from './portfolioEventTransform';
import {
    buildOpenAndPendingPortfolioResponse,
    buildOpenPortfolioEventsByTickerOrder,
    PORTFOLIO_OPEN_PAGE_SIZE,
    type PortfolioOpenAndPendingRouteResponse,
} from './portfolioOpenAndPendingResponse';
import { getUserPropertiesSnapshot, updateUserProperties } from './userProperties';

const HISTORY_PAGE_SIZE = 15;
const EVENT_POSITIONS_PAGE_SIZE = 10000;

const api = createApiClient({
    baseURL: `${config.exchangeApiUrl}/v1`,
});

interface FetchPageResult {
    eventPositions: EventPositionVo[];
    cursor: string | undefined;
}

const fetchEventPositionsPage = async (
    userId: string,
    params: GetEventPositionsParams,
    signal?: AbortSignal,
): Promise<FetchPageResult> => {
    const search = new URLSearchParams();
    if (params.position_status) search.set('position_status', params.position_status);
    if (params.settlement_status) search.set('settlement_status', params.settlement_status);
    if (params.event_tickers) search.set('event_tickers', params.event_tickers);
    if (params.series_ticker) search.set('series_ticker', params.series_ticker);
    if (params.overall_order_by) search.set('overall_order_by', params.overall_order_by);
    if (params.limit != null) search.set('limit', String(params.limit));
    if (params.cursor) search.set('cursor', params.cursor);

    const qs = search.toString();
    const path = `/users/${userId}/event_positions${qs ? `?${qs}` : ''}`;
    const response = await api.get<GetEventPositionsResponse>(path, { signal });
    return {
        eventPositions: response.data.event_positions ?? [],
        cursor: response.data.cursor || undefined,
    };
};

export const fetchAllEventPositions = async (
    userId: string,
    params: GetEventPositionsParams,
    signal?: AbortSignal,
): Promise<EventPositionVo[]> => {
    const eventPositions: EventPositionVo[] = [];
    let cursor: string | undefined;

    for (;;) {
        const page = await fetchEventPositionsPage(
            userId,
            {
                ...params,
                limit: EVENT_POSITIONS_PAGE_SIZE,
                cursor,
                overall_order_by: 'event_ticker',
            },
            signal,
        );

        eventPositions.push(...page.eventPositions);

        if (!page.cursor || page.cursor === cursor) {
            return eventPositions;
        }
        cursor = page.cursor;
    }
};

export const portfolioEventsKeys = {
    all: ['portfolioEvents'] as const,
    openAndPending: (userId: string) => [...portfolioEventsKeys.all, 'openAndPending', userId] as const,
    pendingSearch: (userId: string) => [...portfolioEventsKeys.all, 'pendingSearch', userId] as const,
    settled: (userId: string) => [...portfolioEventsKeys.all, 'settled', userId] as const,
};

export const useResetOpenAndPendingQuery = (): (() => void) => {
    const queryClient = useQueryClient();
    const userId = useAuthStore((s) => s.userId);
    return useCallback(() => {
        queryClient.resetQueries({ queryKey: portfolioEventsKeys.openAndPending(userId ?? '') });
    }, [queryClient, userId]);
};

export interface OpenAndPendingPortfolioEvents {
    /** Open positions, including combo / MVE events inline. */
    open: PortfolioEvent[];
    /** Close+unsettled positions — markets pending payout or settlement. */
    pending: PortfolioEvent[];
    /**
     * Slice of the pending fetch whose positions fully exited before
     * settlement — surfaced under the history view rather than pending.
     */
    earlyExits: PortfolioEvent[];
    /** True when more open pages are available. */
    hasMore: boolean;
    /** Total open event-positions across all pages (from page-1 response). */
    totalOpen: number;
    /** Categories present across all open + pending positions, regardless of the active filter. */
    availableCategories: Category[];
    /** Cursor for portfolio-search mode. Non-search pagination uses ticker slices instead. */
    cursor?: string;
    /**
     * Ordered list of all open event tickers post-sort/filter. Populated on
     * page 1; subsequent pages reuse this list to drive ticker-based fetches
     * via the `/page` endpoint.
     */
    openEventTickers: string[];
}

interface PortfolioEventsPageRouteResponse {
    open: PortfolioEvent[];
}

export interface PortfolioSearchEventPositionsResponse {
    event_positions: EventPositionVo[];
    events: EventVo[];
    next_cursor?: string;
    total_results_count: number;
}

const throwPortfolioRouteError = (routeName: string, response: Response): never => {
    throw new Error(`${routeName} returned ${response.status}`);
};

const shouldFetchPortfolioEventsDirectly = (): boolean =>
    typeof window !== 'undefined' && window.location.hostname === 'localhost';

/** Page size — must match the server-side default in `/event-positions`. */
const OPEN_PAGE_SIZE = PORTFOLIO_OPEN_PAGE_SIZE;

type OpenAndPendingQueryResult = UseInfiniteQueryResult<InfiniteData<OpenAndPendingPortfolioEvents>, Error> & {
    hasMore: boolean;
    totalOpen: number;
    isFirstPageLoaded: boolean;
};

interface PendingPortfolioSearchEvents {
    pending: PortfolioEvent[];
    cursor: string | undefined;
}

type PendingPortfolioSearchQueryResult = UseInfiniteQueryResult<InfiniteData<PendingPortfolioSearchEvents>, Error> & {
    hasMore: boolean;
    isFirstPageLoaded: boolean;
};

/**
 * Localhost cannot send the exchange API's session cookies to the same-origin
 * Next proxy, so it fetches raw exchange data directly and then uses the same
 * pure portfolio assembly helper as the proxy route.
 */
const fetchEventsMapDirect = async (eventTickers: string[], signal?: AbortSignal): Promise<Map<string, EventVo>> => {
    const tickers = [...new Set(eventTickers.filter(Boolean))];
    if (tickers.length === 0) return new Map();

    const chunks: string[][] = [];
    for (let i = 0; i < tickers.length; i += 200) {
        chunks.push(tickers.slice(i, i + 200));
    }

    const eventArrays = await Promise.all(
        chunks.map(async (chunk) => {
            const response = await api.get<GetEventsResponse>(`/events/?tickers=${chunk.join(',')}`, { signal });
            return response.data.events ?? [];
        }),
    );

    const events = eventArrays.flat();
    enrichCloseDates(events);
    return new Map(events.map((e) => [e.ticker, e]));
};

const fetchOpenAndPendingDirect = async (
    userId: string,
    sortOptions: SortPortfolioEventsOptions,
    category: Category | undefined,
    signal?: AbortSignal,
): Promise<PortfolioOpenAndPendingRouteResponse> => {
    const [openResult, pendingResult] = await Promise.all([
        fetchAllEventPositions(userId, { position_status: 'open' }, signal),
        fetchAllEventPositions(userId, { position_status: 'close', settlement_status: 'unsettled' }, signal),
    ]);

    const unfilteredOpen = openResult;
    const unfilteredPending = pendingResult;
    const allTickers = [
        ...new Set([...unfilteredOpen, ...unfilteredPending].map((p) => p.event_ticker).filter(Boolean)),
    ];
    const eventsMap = await fetchEventsMapDirect(allTickers, signal);

    return buildOpenAndPendingPortfolioResponse({
        eventsMap,
        openPositions: unfilteredOpen,
        pendingPositions: unfilteredPending,
        sortOptions,
        category,
    });
};

// Upstream caps `event_tickers` at 20; mirror the production route's chunk size.
const DIRECT_FETCH_CHUNK_SIZE = 20;

const fetchOpenEventsByTickersDirect = async (
    userId: string,
    eventTickers: string[],
    signal?: AbortSignal,
): Promise<PortfolioEvent[]> => {
    const chunks: string[][] = [];
    for (let i = 0; i < eventTickers.length; i += DIRECT_FETCH_CHUNK_SIZE) {
        chunks.push(eventTickers.slice(i, i + DIRECT_FETCH_CHUNK_SIZE));
    }

    const chunkResults = await Promise.all(
        chunks.map((chunk) =>
            fetchEventPositionsPage(
                userId,
                {
                    position_status: 'open',
                    event_tickers: chunk.join(','),
                    limit: chunk.length,
                },
                signal,
            ),
        ),
    );

    const eventPositions = chunkResults.flatMap((r) => r.eventPositions);
    const eventsMap = await fetchEventsMapDirect(eventTickers, signal);
    return buildOpenPortfolioEventsByTickerOrder(eventsMap, eventPositions, eventTickers);
};

/**
 * Page 1 of the open-positions list: pulls open + pending event-positions
 * via the main route, which returns the first slice plus the full ordered
 * `openEventTickers` list for later ticker-based pagination.
 */
const fetchOpenAndPendingViaRoute = async (
    userId: string | null,
    sortOptions: SortPortfolioEventsOptions,
    category: Category | undefined,
    subaccount_number: number,
    signal?: AbortSignal,
): Promise<PortfolioOpenAndPendingRouteResponse> => {
    if (!userId) {
        return {
            open: [],
            pending: [],
            earlyExits: [],
            hasMore: false,
            totalOpen: 0,
            availableCategories: [],
            openEventTickers: [],
            openCombos: [],
            hasMoreCombos: false,
            totalOpenCombos: 0,
            openCombosEventTickers: [],
        };
    }

    if (shouldFetchPortfolioEventsDirectly()) {
        return fetchOpenAndPendingDirect(userId, sortOptions, category, signal);
    }

    const csrfToken = getLocalStorageWithExpiration(CSRF_LOCAL_STORAGE_KEY);
    const response = await fetchWithAwsWaf('/api-app/portfolio/event-positions', {
        method: 'POST',
        credentials: 'include',
        headers: {
            'content-type': 'application/json',
            ...(csrfToken && { [CSRF_HEADER_NAME]: csrfToken }),
        },
        body: JSON.stringify({
            userId,
            subaccount_number: subaccount_number || undefined,
            sortOptions: {
                criteria: sortOptions.criteria,
                reversed: sortOptions.reversed,
                priceChangePeriod: sortOptions.priceChangePeriod,
                positionValueChangePeriod: sortOptions.positionValueChangePeriod,
            },
            category,
            pageNumber: 1,
        }),
        signal,
    });
    if (!response.ok) {
        throwPortfolioRouteError('portfolio event-positions route', response);
    }
    return (await response.json()) as PortfolioOpenAndPendingRouteResponse;
};

/**
 * Subsequent pages of the open-positions list: fetches positions + events
 * for a specific ordered ticker slice via the `/page` endpoint. The response
 * preserves the request's ticker order, so callers can append directly to
 * their bucket without re-sorting.
 */
export const fetchOpenEventsByTickers = async (
    userId: string,
    eventTickers: string[],
    subaccount_number: number,
    signal?: AbortSignal,
): Promise<PortfolioEvent[]> => {
    if (eventTickers.length === 0) return [];

    if (shouldFetchPortfolioEventsDirectly()) {
        return fetchOpenEventsByTickersDirect(userId, eventTickers, signal);
    }

    const csrfToken = getLocalStorageWithExpiration(CSRF_LOCAL_STORAGE_KEY);
    const response = await fetchWithAwsWaf('/api-app/portfolio/event-positions/page', {
        method: 'POST',
        credentials: 'include',
        headers: {
            'content-type': 'application/json',
            ...(csrfToken && { [CSRF_HEADER_NAME]: csrfToken }),
        },
        body: JSON.stringify({ userId, subaccount_number: subaccount_number || undefined, eventTickers }),
        signal,
    });
    if (!response.ok) {
        throwPortfolioRouteError('portfolio event-positions/page route', response);
    }
    const data = (await response.json()) as PortfolioEventsPageRouteResponse;
    return data.open;
};

export const buildPortfolioSearchPath = (
    userId: string,
    query: string,
    positionStatus: Exclude<PositionStatus, undefined>,
    settlementStatus: SettlementStatus | undefined,
    category: Category | undefined,
    subaccountNumber: number,
    cursor: string | undefined,
    pageSize: number = OPEN_PAGE_SIZE,
): string => {
    const search = new URLSearchParams();
    search.set('query', query);
    search.set('skip_embedding_search', 'true');
    search.set('page_size', String(pageSize));
    search.set('position_status', positionStatus);
    if (settlementStatus) search.set('settlement_status', settlementStatus);
    if (category) search.set('category', category);
    if (cursor) search.set('cursor', cursor);
    if (subaccountNumber) search.set('subaccount_number', String(subaccountNumber));

    return `/users/${encodeURIComponent(userId)}/portfolio/search/event_positions?${search.toString()}`;
};

const fetchPortfolioSearchPage = async (
    userId: string,
    query: string,
    positionStatus: Exclude<PositionStatus, undefined>,
    settlementStatus: SettlementStatus | undefined,
    category: Category | undefined,
    subaccountNumber: number,
    cursor: string | undefined,
    signal?: AbortSignal,
): Promise<PortfolioSearchEventPositionsResponse> => {
    const path = buildPortfolioSearchPath(
        userId,
        query,
        positionStatus,
        settlementStatus,
        category,
        subaccountNumber,
        cursor,
    );
    const response = await api.get<PortfolioSearchEventPositionsResponse>(path, { signal });
    return response.data;
};

const eventsMapFromSearch = (response: PortfolioSearchEventPositionsResponse): Map<string, EventVo> => {
    const events = response.events ?? [];
    enrichCloseDates(events);
    return new Map(events.map((event) => [event.ticker, event]));
};

const availableCategoriesFromPortfolioEvents = (portfolioEvents: PortfolioEvent[]): Category[] => {
    const available = new Set(portfolioEvents.map((event) => event.category));
    return categories.filter((category) => available.has(category));
};

export const toOpenPortfolioEventsFromSearchResponse = (
    openResponse: PortfolioSearchEventPositionsResponse,
): OpenAndPendingPortfolioEvents => {
    const openEventsMap = eventsMapFromSearch(openResponse);
    const openPositions = openResponse.event_positions ?? [];
    const open = toPortfolioEventsFromVos(openEventsMap, openPositions, 'open');

    return {
        open,
        pending: [],
        earlyExits: [],
        hasMore: Boolean(openResponse.next_cursor),
        totalOpen: openResponse.total_results_count,
        availableCategories: availableCategoriesFromPortfolioEvents(open),
        cursor: openResponse.next_cursor || undefined,
        openEventTickers: [],
    };
};

export const toPendingPortfolioEventsFromSearchResponse = (
    pendingResponse: PortfolioSearchEventPositionsResponse,
): PendingPortfolioSearchEvents => {
    const pendingEventsMap = eventsMapFromSearch(pendingResponse);
    const pending = toPortfolioEventsFromVos(pendingEventsMap, pendingResponse.event_positions ?? [], 'pending');

    return {
        pending,
        cursor: pendingResponse.next_cursor || undefined,
    };
};

const fetchOpenViaPortfolioSearch = async (
    userId: string,
    query: string,
    category: Category | undefined,
    subaccountNumber: number,
    cursor: string | undefined,
    signal?: AbortSignal,
): Promise<OpenAndPendingPortfolioEvents> => {
    const openResponse = await fetchPortfolioSearchPage(
        userId,
        query,
        'open',
        undefined,
        category,
        subaccountNumber,
        cursor,
        signal,
    );
    return toOpenPortfolioEventsFromSearchResponse(openResponse);
};

const fetchPendingViaPortfolioSearch = async (
    userId: string,
    query: string,
    category: Category | undefined,
    subaccountNumber: number,
    cursor: string | undefined,
    signal?: AbortSignal,
): Promise<PendingPortfolioSearchEvents> => {
    const pendingResponse = await fetchPortfolioSearchPage(
        userId,
        query,
        'close',
        'unsettled',
        category,
        subaccountNumber,
        cursor,
        signal,
    );
    return toPendingPortfolioEventsFromSearchResponse(pendingResponse);
};

/**
 * Load the user's open + pending event positions together, transform each
 * bucket into `PortfolioEvent[]`, and dispatch accumulated pages into
 * `usePortfolioEventsStore`. Supports infinite scroll — call `fetchNextPage`
 * when the user scrolls to the bottom of the open-positions list.
 */
export const useOpenAndPendingPortfolioEventsQuery = (
    sortOptions: SortPortfolioEventsOptions,
    category?: Category,
    searchTerm?: string,
): OpenAndPendingQueryResult => {
    const userId = useAuthStore((s) => s.userId);
    const isMember = useAuthStore((s) => s.isLoggedInMember);
    const subaccountNumber = useSubAccountStore((s) => s.subaccountNumber);
    const {
        openEventsLoaded,
        pendingEventsLoaded,
        nonSearchOpenPendingCountsLoaded,
        openLoadingChanged,
        pendingLoadingChanged,
        availableCategoriesLoaded,
    } = usePortfolioEventsStore((s) => s.actions);
    const normalizedSearchTerm = searchTerm?.trim() ?? '';
    const isSearchMode = normalizedSearchTerm.length > 0;
    const categoryKey = category ?? null;
    const sortCriteriaKey = isSearchMode ? 'relevance' : sortOptions.criteria;
    const sortReversedKey = isSearchMode ? false : (sortOptions.reversed ?? false);
    const priceChangePeriodKey = isSearchMode ? null : (sortOptions.priceChangePeriod ?? null);
    const positionValueChangePeriodKey = isSearchMode ? null : (sortOptions.positionValueChangePeriod ?? null);

    // `pageParam` is `null` for the first page. Non-search follow-up pages
    // use ticker slices for the `/page` route; search follow-up pages use
    // the backend cursor from `/portfolio/search/event_positions`.
    type PageParam = string[] | string | null;

    const query = useInfiniteQuery<
        OpenAndPendingPortfolioEvents,
        Error,
        InfiniteData<OpenAndPendingPortfolioEvents>,
        readonly unknown[],
        PageParam
    >({
        // Including `category` and the trimmed search term in the key gives
        // each filter its own cache entry — switching back to a previously-
        // viewed search/category combo serves the cached page-1 immediately
        // (with `keepPreviousData` cushioning the refetch).
        queryKey: [
            ...portfolioEventsKeys.openAndPending(userId ?? ''),
            categoryKey,
            normalizedSearchTerm,
            sortCriteriaKey,
            sortReversedKey,
            priceChangePeriodKey,
            positionValueChangePeriodKey,
            subaccountNumber,
        ],
        initialPageParam: null,
        queryFn: async ({ pageParam, signal }) => {
            if (isSearchMode) {
                if (!userId) {
                    return {
                        open: [],
                        pending: [],
                        earlyExits: [],
                        hasMore: false,
                        totalOpen: 0,
                        availableCategories: [],
                        openEventTickers: [],
                    };
                }
                return fetchOpenViaPortfolioSearch(
                    userId,
                    normalizedSearchTerm,
                    category,
                    subaccountNumber,
                    typeof pageParam === 'string' ? pageParam : undefined,
                    signal,
                );
            }
            if (pageParam === null) {
                // Page 1: full open + pending fetch including the ordered
                // ticker list used by subsequent pages.
                return fetchOpenAndPendingViaRoute(userId, sortOptions, category, subaccountNumber, signal);
            }
            if (!userId || typeof pageParam === 'string' || pageParam.length === 0) {
                return {
                    open: [],
                    pending: [],
                    earlyExits: [],
                    hasMore: false,
                    totalOpen: 0,
                    availableCategories: [],
                    openEventTickers: [],
                };
            }
            const open = await fetchOpenEventsByTickers(userId, pageParam, subaccountNumber, signal);
            return {
                open,
                // Pending + early exits are page-1 only; subsequent pages
                // leave them empty so the in-store slices keep the page-1
                // values (the effect below only writes pending from the
                // first page).
                pending: [],
                earlyExits: [],
                hasMore: false, // overridden by getNextPageParam below
                totalOpen: 0,
                availableCategories: [],
                openEventTickers: [],
            };
        },
        getNextPageParam: (_lastPage, allPages) => {
            const firstPage = allPages[0];
            if (!firstPage) return undefined;
            if (isSearchMode) {
                const lastPage = allPages[allPages.length - 1];
                return lastPage?.cursor ?? undefined;
            }
            const allTickers = firstPage.openEventTickers;
            // Each page covers a fixed `OPEN_PAGE_SIZE` slice of the ticker
            // list — even if the resulting `open` row count is smaller (some
            // tickers may have no rows that match the bucket filter), the
            // slice itself advances by `OPEN_PAGE_SIZE` per page.
            const consumed = allPages.length * OPEN_PAGE_SIZE;
            if (consumed >= allTickers.length) return undefined;
            return allTickers.slice(consumed, consumed + OPEN_PAGE_SIZE);
        },
        enabled: !!userId && isMember,
        placeholderData: keepPreviousData,
    });

    useEffect(() => {
        openLoadingChanged(query.isFetching);
        if (!isSearchMode) pendingLoadingChanged(query.isFetching);
    }, [query.isFetching, isSearchMode, openLoadingChanged, pendingLoadingChanged]);

    useEffect(() => {
        if (!query.data || query.isPlaceholderData) return;
        const allOpen = query.data.pages.flatMap((p) => p.open);
        const firstPage = query.data.pages[0];
        // Forward `openEventTickers` from page 1 — the canonical full list
        // of open event tickers the server knows about post-sort/filter.
        // The portfolio store consults this to tell brand-new
        // `user_position` events (need to fetch + prepend) apart from
        // events that just haven't been paginated in yet.
        openEventsLoaded(allOpen, firstPage?.openEventTickers ?? []);
        if (firstPage) {
            if (!isSearchMode) {
                pendingEventsLoaded(firstPage.pending, firstPage.earlyExits);
                nonSearchOpenPendingCountsLoaded(firstPage.totalOpen, firstPage.pending.length);
                availableCategoriesLoaded(firstPage.availableCategories);
            }
        }
    }, [
        query.data,
        query.isPlaceholderData,
        isSearchMode,
        openEventsLoaded,
        pendingEventsLoaded,
        nonSearchOpenPendingCountsLoaded,
        availableCategoriesLoaded,
    ]);

    const isFirstPageLoaded = (query.data?.pages.length ?? 0) > 0;
    const totalOpen = query.data?.pages[0]?.totalOpen ?? 0;

    return Object.assign(query, { hasMore: !!query.hasNextPage, totalOpen, isFirstPageLoaded });
};

export const usePendingPortfolioSearchQuery = (
    category?: Category,
    searchTerm?: string,
): PendingPortfolioSearchQueryResult => {
    const userId = useAuthStore((s) => s.userId);
    const isMember = useAuthStore((s) => s.isLoggedInMember);
    const subaccountNumber = useSubAccountStore((s) => s.subaccountNumber);
    const { pendingSearchEventsLoaded, pendingLoadingChanged } = usePortfolioEventsStore((s) => s.actions);
    const normalizedSearchTerm = searchTerm?.trim() ?? '';
    const isSearchMode = normalizedSearchTerm.length > 0;

    const query = useInfiniteQuery<
        PendingPortfolioSearchEvents,
        Error,
        InfiniteData<PendingPortfolioSearchEvents>,
        readonly unknown[],
        string | undefined
    >({
        queryKey: [
            ...portfolioEventsKeys.pendingSearch(userId ?? ''),
            category ?? null,
            normalizedSearchTerm,
            subaccountNumber,
        ],
        initialPageParam: undefined,
        queryFn: ({ pageParam, signal }) => {
            if (!userId) return { pending: [], cursor: undefined };
            return fetchPendingViaPortfolioSearch(
                userId,
                normalizedSearchTerm,
                category,
                subaccountNumber,
                pageParam,
                signal,
            );
        },
        getNextPageParam: (lastPage) => lastPage.cursor ?? undefined,
        enabled: !!userId && isMember && isSearchMode,
        placeholderData: keepPreviousData,
    });

    useEffect(() => {
        pendingLoadingChanged(isSearchMode && query.isFetching);
    }, [isSearchMode, query.isFetching, pendingLoadingChanged]);

    useEffect(() => {
        if (!isSearchMode || !query.data || query.isPlaceholderData) return;
        pendingSearchEventsLoaded(query.data.pages.flatMap((page) => page.pending));
    }, [isSearchMode, query.data, query.isPlaceholderData, pendingSearchEventsLoaded]);

    return Object.assign(query, {
        hasMore: !!query.hasNextPage,
        isFirstPageLoaded: (query.data?.pages.length ?? 0) > 0,
    });
};

interface SettledPageData {
    events: PortfolioEvent[];
    cursor: string | undefined;
}

type SettledQueryResult = UseInfiniteQueryResult<InfiniteData<SettledPageData>, Error>;

/**
 * Load the settled history portfolio events with cursor-based pagination,
 * transform each page into `PortfolioEvent[]`, and dispatch accumulated
 * results into `usePortfolioEventsStore`.
 *
 * Returns the underlying infinite query so consumers can call `fetchNextPage`
 * for "load more" interactions and read `hasMore` to know when to stop.
 */
export const useSettledPortfolioEventsQuery = (): SettledQueryResult & { hasMore: boolean } => {
    const userId = useAuthStore((s) => s.userId);
    const isMember = useAuthStore((s) => s.isLoggedInMember);
    const subaccountNumber = useSubAccountStore((s) => s.subaccountNumber);
    const { settledEventsLoaded, settledLoadingChanged, updateClosedTabCounts } = usePortfolioEventsStore(
        (s) => s.actions,
    );

    const query = useInfiniteQuery<
        SettledPageData,
        Error,
        InfiniteData<SettledPageData>,
        readonly unknown[],
        string | undefined
    >({
        queryKey: [...portfolioEventsKeys.settled(userId ?? ''), subaccountNumber],
        initialPageParam: undefined,
        queryFn: async ({ pageParam, signal }) => {
            if (!userId) return { events: [], cursor: undefined };
            const { eventPositions, cursor } = await fetchEventPositionsPage(
                userId,
                {
                    position_status: 'close',
                    settlement_status: 'settled',
                    limit: HISTORY_PAGE_SIZE,
                    cursor: pageParam,
                },
                signal,
            );

            const tickers = [...new Set(eventPositions.map((p) => p.event_ticker).filter(Boolean))];
            let eventsMap = new Map<string, EventVo>();
            if (tickers.length > 0) {
                const eventsResponse = await api.get<GetEventsResponse>(`/events/?tickers=${tickers.join(',')}`, {
                    signal,
                });
                const fetchedEvents = eventsResponse.data.events ?? [];
                enrichCloseDates(fetchedEvents);
                eventsMap = new Map(fetchedEvents.map((e) => [e.ticker, e]));
            }

            if (!pageParam) {
                updateClosedTabCounts(eventPositions, getUserPropertiesSnapshot().portfolioClosedTabMarketIds, (ids) =>
                    updateUserProperties({ portfolioClosedTabMarketIds: ids }),
                );
            }
            return {
                events: toPortfolioEventsFromVos(eventsMap, eventPositions, 'history'),
                cursor,
            };
        },
        // Treat an empty cursor (or "" from the upstream) as "no more pages".
        // Returning a falsy value here makes React Query mark `hasNextPage`
        // false, which is what the IntersectionObserver gate keys off.
        getNextPageParam: (lastPage) => lastPage.cursor ?? undefined,
        enabled: !!userId && isMember,
        // Cushion against the broad `portfolioEventsKeys.all` invalidation in
        // `tradingSync` (navigation / visibility / WS reconnect): without a
        // staleTime, every invalidation refetches every loaded page from
        // scratch, which manifested as the first page being requested 2-3
        // times back-to-back on portfolio mount.
        staleTime: 30_000,
        placeholderData: keepPreviousData,
    });

    useEffect(() => {
        settledLoadingChanged(query.isFetching);
    }, [query.isFetching, settledLoadingChanged]);

    useEffect(() => {
        if (!query.data || query.isPlaceholderData) {
            settledEventsLoaded([], undefined);
            return;
        }
        const flatEvents = query.data.pages.flatMap((p) => p.events);
        const lastCursor = query.data.pages[query.data.pages.length - 1]?.cursor;
        settledEventsLoaded(flatEvents, lastCursor);
    }, [query.data, query.isPlaceholderData, settledEventsLoaded]);

    return Object.assign(query, { hasMore: !!query.hasNextPage });
};
